import { KYC_COPY, declarationParts, cardRows, type DoneModel, type KycCard, type Row } from "../app/kyc-model.js";
import { el } from "../lib/dom.js";
import { icon } from "../lib/icon.js";
import { setBusy } from "./busy.js";
import { cardSkeleton, LOADING_CARD } from "./check-sign.js";

/**
 * DCTA-176 F46 — the identity-check refresh's screens (docs/design/v3/04-kyc.md §0.2, §2.2; mock-up 22-kyc.html; shared.css
 * §10.9 and §12). A flow presented from the bottom: one task per screen, a close button, the primary action pinned in the
 * form bar. Four stacked screens — Start (1 of 3), Details (2 of 3), Check and sign (3 of 3), Done — and the consent
 * placeholder that stands where the Singpass app's own screen is in real life (the system's style, never Singpass's).
 *
 * The flow is a stacked screen, never a sheet over the chat: while it is up, the composer and the assistant's mark are not
 * on screen (§0.2 Layering). Every string from the bank is set as text (`el` builds nodes, never markup). What is drawn
 * is the controller's (`app/kyc-flow.ts`); this file is only the screens.
 */
export interface KycViewHandlers {
  onClose(): void;
  onGetMyinfo(): void;
  onTalk(): void;
  onShare(): void;
  onCancelConsent(): void;
  onBackToStart(): void;
  onReview(): void;
  onBackToDetails(): void;
  /** The tap itself: the controller's first statement starts the passkey ceremony (Safari's user-gesture rule). */
  onSign(): void;
  onNotNow(): void;
  onDone(): void;
}

/** Start's one line when something stopped the last try — neutral for the customer's own choice, loud for a failure. */
export type StartNote = "declined" | "unavailable" | "expired" | "noBank";

type Screen = "start" | "details" | "sign" | "done";

/** 25i's lines on Start, each a bold lead and the rest. */
const NOTES: Readonly<Record<StartNote, { readonly lead: string; readonly rest: string }>> = {
  declined: { lead: KYC_COPY.declined, rest: "" },
  unavailable: KYC_COPY.unavailable,
  expired: { lead: KYC_COPY.expired, rest: "" },
  noBank: KYC_COPY.noBank,
};

function topbar(lead: HTMLElement | undefined, title: string, id: string): { bar: HTMLElement; title: HTMLElement } {
  const t = el("span", { class: "t", id, tabIndex: -1 }, [title]);
  return { bar: el("div", { class: "topbar" }, [lead ?? el("span", { class: "topbar-pad", aria: { hidden: "true" } }), t, el("span", { class: "topbar-pad", aria: { hidden: "true" } })]), title: t };
}

function iconButton(cls: string, label: string, id: "i-x" | "i-chev-l", onClick: () => void): HTMLButtonElement {
  const b = el("button", { class: `raw icobtn ${cls}`, type: "button", aria: { label } }, [icon(id)]);
  b.addEventListener("click", onClick);
  return b;
}

function button(cls: string, label: string, onClick: () => void): HTMLButtonElement {
  const b = el("button", { class: `raw btn ${cls}`, type: "button" }, [label]);
  b.addEventListener("click", onClick);
  return b;
}

/** A key–value row: the label, then the value — several lines (an address) each on its own line. */
function kvRow(r: Row): HTMLElement {
  const lines: (string | HTMLElement)[] = [];
  r.v.forEach((line, i) => {
    if (i > 0) lines.push(el("br"));
    lines.push(line);
  });
  return el("div", {}, [el("dt", { class: "k" }, [r.k]), el("dd", { class: "v" }, lines)]);
}

export class KycFlowView {
  readonly parts: readonly HTMLElement[];
  readonly #h: KycViewHandlers;
  readonly #screens: HTMLElement;
  readonly #scr: Record<Screen, HTMLElement>;
  readonly #titles: Record<Screen, HTMLElement>;
  #on: Screen = "start";
  // Start
  readonly #note: HTMLElement;
  readonly #noteLead: HTMLElement;
  readonly #noteRest: HTMLElement;
  readonly #heroTitle: HTMLElement;
  readonly #heroLead: HTMLElement;
  readonly #getMyinfo: HTMLButtonElement;
  // Details
  readonly #dBody: HTMLElement;
  readonly #dNote: HTMLElement;
  readonly #mi: HTMLElement;
  readonly #mih: HTMLElement;
  readonly #you: HTMLElement;
  readonly #skelYou: HTMLElement;
  readonly #occ: HTMLInputElement;
  readonly #occBox: HTMLElement;
  readonly #occErr: HTMLElement;
  readonly #funds: HTMLElement;
  readonly #fundsErr: HTMLElement;
  readonly #review: HTMLButtonElement;
  // Check and sign
  readonly #sBody: HTMLElement;
  readonly #sNote: HTMLElement;
  readonly #slot: HTMLElement;
  #sign: HTMLButtonElement | undefined;
  #signLine: HTMLElement | undefined;
  #cancelled: HTMLElement | undefined;
  // Done
  readonly #doneAgain: HTMLElement;
  readonly #doneTitle: HTMLElement;
  readonly #doneHero: HTMLElement;
  readonly #doneSec: HTMLElement;
  readonly #doneRows: HTMLElement;
  // The consent placeholder
  readonly #backdrop: HTMLElement;
  readonly #consent: HTMLElement;
  readonly #share: HTMLButtonElement;
  #consentFrom: HTMLElement | undefined;

  constructor(handlers: KycViewHandlers) {
    this.#h = handlers;

    // ------------------------------------------------------------------ Start
    const s = topbar(iconButton("close", "Close", "i-x", () => this.#h.onClose()), KYC_COPY.title, "kyc-t1");
    this.#noteLead = el("b");
    this.#noteRest = el("span");
    this.#note = el("div", { class: "banner kerr", role: "status", hidden: true }, [icon("i-alert"), el("span", { class: "bt" }, [this.#noteLead, this.#noteRest])]);
    this.#heroTitle = el("h2", { class: "kh" });
    this.#heroLead = el("p", { class: "kp" });
    this.#getMyinfo = button("getmi", KYC_COPY.getMyinfo, () => this.#h.onGetMyinfo());
    const start = el("div", { class: "scr s-start kycflow", role: "region", aria: { labelledby: "kyc-t1", describedby: "kyc-s1" } }, [
      s.bar,
      el("div", { class: "body" }, [
        el("p", { class: "step", id: "kyc-s1" }, [KYC_COPY.of(1)]),
        this.#note,
        el("div", { class: "kyc-hero" }, [el("span", { class: "mk", aria: { hidden: "true" } }, [icon("i-id")]), this.#heroTitle, this.#heroLead]),
        el(
          "div",
          { class: "list feed seq" },
          KYC_COPY.parts.map(([title, sub], i) =>
            el("div", { class: "item" }, [el("span", { class: "ic", aria: { hidden: "true" } }, [String(i + 1)]), el("span", { class: "tx" }, [el("b", {}, [title]), el("small", {}, [sub])])]),
          ),
        ),
        el("p", { class: "warnline" }, [icon("i-shield"), el("span", {}, [KYC_COPY.safety])]),
        el("div", { class: "home-end", aria: { hidden: "true" } }),
      ]),
      el("div", { class: "formbar" }, [this.#getMyinfo, button("link talk", KYC_COPY.talkStart, () => this.#h.onTalk()), el("p", { class: "demo" }, [KYC_COPY.demo])]),
    ]);

    // ---------------------------------------------------------------- Details
    const d = topbar(iconButton("back", "Back to the start", "i-chev-l", () => this.#h.onBackToStart()), KYC_COPY.title, "kyc-t2");
    this.#dNote = el("span", { class: "vis-hidden loading-note", role: "status" });
    this.#mi = el("dl", { class: "list kv mi" });
    this.#mih = el("p", { class: "help below mih" }, [KYC_COPY.readOnly]);
    this.#occ = el("input", { id: "kyc-occ", class: "q", type: "text", autocomplete: "organization-title", maxLength: 60, aria: { describedby: "kyc-occ-err" } }) as HTMLInputElement;
    this.#occ.setAttribute("enterkeyhint", "done");
    this.#occ.setAttribute("autocapitalize", "sentences");
    this.#occ.addEventListener("input", () => this.occupationError(undefined));
    this.#occBox = el("label", { class: "inp field" }, [this.#occ]);
    this.#occErr = el("p", { class: "err", id: "kyc-occ-err", role: "alert", hidden: true });
    this.#funds = el("div", { class: "filters sf" });
    this.#fundsErr = el("p", { class: "err", role: "alert", hidden: true });
    this.#you = el("div", { class: "you" }, [
      el("div", { class: "sec" }, [el("h3", {}, [KYC_COPY.fromYou])]),
      el("div", { class: "fld occ" }, [el("label", { class: "l", for: "kyc-occ" }, [KYC_COPY.occupation]), this.#occBox, this.#occErr]),
      el("div", { class: "fld sfg", role: "group", aria: { labelledby: "kyc-sf-l", describedby: "kyc-sf-h" } }, [
        el("span", { class: "l", id: "kyc-sf-l" }, [KYC_COPY.moneyFrom]),
        this.#funds,
        el("span", { class: "help", id: "kyc-sf-h" }, [KYC_COPY.pickAll]),
        this.#fundsErr,
      ]),
    ]);
    this.#skelYou = el("div", { class: "skel-you", aria: { hidden: "true" }, hidden: true }, [
      el("div", { class: "sec" }, [el("h3", {}, [KYC_COPY.fromYou])]),
      el("span", { class: "sk sk-l" }),
      el("span", { class: "sk sk-field" }),
      el("span", { class: "sk sk-l wide" }),
      el("span", { class: "sk-chips" }, [el("span", { class: "sk sk-chip" }), el("span", { class: "sk sk-chip" }), el("span", { class: "sk sk-chip" })]),
    ]);
    this.#review = button("review", KYC_COPY.review, () => {
      if (this.#review.getAttribute("aria-disabled") === "true") return;
      this.#h.onReview();
    });
    this.#dBody = el("div", { class: "body" }, [
      el("p", { class: "step", id: "kyc-s2" }, [KYC_COPY.of(2)]),
      this.#dNote,
      el("div", { class: "sec" }, [el("h3", {}, [KYC_COPY.fromMyinfo]), el("span", { class: "flag" }, [KYC_COPY.demoFlag])]),
      this.#mi,
      this.#mih,
      this.#skelYou,
      this.#you,
      el("div", { class: "home-end", aria: { hidden: "true" } }),
    ]);
    const details = el("div", { class: "scr s-details kycflow off-r", role: "region", aria: { labelledby: "kyc-t2", describedby: "kyc-s2", hidden: "true" } }, [
      d.bar,
      this.#dBody,
      el("div", { class: "formbar" }, [this.#review, button("link talk", KYC_COPY.talkDetails, () => this.#h.onTalk())]),
    ]);

    // --------------------------------------------------------- Check and sign
    const g = topbar(iconButton("back", "Back to your details", "i-chev-l", () => this.#h.onBackToDetails()), KYC_COPY.signTitle, "kyc-t3");
    this.#sNote = el("span", { class: "vis-hidden loading-note", role: "status" });
    this.#slot = el("div", { class: "signslot" });
    this.#sBody = el("div", { class: "body" }, [el("p", { class: "step", id: "kyc-s3" }, [KYC_COPY.of(3)]), this.#sNote, this.#slot, el("div", { class: "home-end", aria: { hidden: "true" } })]);
    const sign = el("div", { class: "scr s-sign kycflow off-r", role: "region", aria: { labelledby: "kyc-t3", describedby: "kyc-s3", hidden: "true" } }, [g.bar, this.#sBody]);

    // ------------------------------------------------------------------- Done
    const n = topbar(undefined, KYC_COPY.title, "kyc-t4");
    this.#doneTitle = el("h2", { id: "kyc-h4", tabIndex: -1 });
    this.#doneAgain = el("p", { class: "strong again", hidden: true }, [KYC_COPY.doneAgain]);
    this.#doneHero = el("div", { class: "kyc-hero ok", role: "status" }, [el("span", { class: "mk", aria: { hidden: "true" } }, [icon("i-check")]), this.#doneTitle, this.#doneAgain]);
    this.#doneSec = el("div", { class: "sec", hidden: true }, [el("h3", {}, [KYC_COPY.holds])]);
    this.#doneRows = el("dl", { class: "list kv", hidden: true });
    const done = el("div", { class: "scr s-done kycflow off-r", role: "region", aria: { labelledby: "kyc-h4", hidden: "true" } }, [
      n.bar,
      el("div", { class: "body" }, [this.#doneHero, this.#doneSec, this.#doneRows, el("div", { class: "home-end", aria: { hidden: "true" } })]),
      el("div", { class: "formbar" }, [button("done", KYC_COPY.done, () => this.#h.onDone())]),
    ]);

    this.#scr = { start, details, sign, done };
    this.#titles = { start: s.title, details: d.title, sign: g.title, done: this.#doneTitle };
    for (const k of ["details", "sign", "done"] as const) this.#scr[k].inert = true;

    // ------------------------------------------------- the consent placeholder
    this.#share = el("button", { class: "raw b ok share", type: "button" }, [KYC_COPY.share]);
    this.#share.addEventListener("click", () => this.#h.onShare());
    const no = el("button", { class: "raw b plain no", type: "button" }, [KYC_COPY.cancel]);
    no.addEventListener("click", () => this.#h.onCancelConsent());
    this.#consent = el("div", { class: "os consent", role: "dialog", aria: { modal: "true", labelledby: "kyc-os-t" }, hidden: true }, [
      el("div", { class: "ttl", id: "kyc-os-t" }, [KYC_COPY.consentTitle]),
      el("ul", { class: "items" }, KYC_COPY.consentItems.map((i) => el("li", {}, [i]))),
      this.#share,
      no,
      el("div", { class: "fake" }, [KYC_COPY.consentFake]),
    ]);
    this.#consent.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        this.#h.onCancelConsent();
        return;
      }
      // Focus stays in the prompt: Tab moves between its two buttons only.
      if (e.key === "Tab") {
        e.preventDefault();
        (document.activeElement === this.#share ? no : this.#share).focus();
      }
    });
    this.#backdrop = el("div", { class: "backdrop" });

    this.#screens = el("div", { class: "screens kycscreens" }, [start, details, sign, done]);
    this.#screens.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      this.#h.onClose();
    });
    this.parts = [this.#screens, this.#backdrop, this.#consent];
  }

  /** The screen on top. */
  get screen(): Screen {
    return this.#on;
  }

  get consentOpen(): boolean {
    return !this.#consent.hidden;
  }

  /** The screen on top's title takes focus — called once the flow is in the page (a node outside it cannot take focus). */
  focus(): void {
    this.#titles[this.#on].focus({ preventScroll: true });
  }

  // ---------------------------------------------------------------- moving
  /** To another screen: forward pushes in from the right over the one it leaves; back pops it off. Focus to its title. */
  go(to: Screen, focus = true): void {
    const order: readonly Screen[] = ["start", "details", "sign", "done"];
    if (this.#on !== to) {
      for (const k of order) {
        const scr = this.#scr[k];
        const on = k === to;
        const before = order.indexOf(k) < order.indexOf(to);
        scr.classList.toggle("off-r", !on && !before);
        scr.classList.toggle("under", !on && before);
        scr.inert = !on;
        if (on) scr.removeAttribute("aria-hidden");
        else scr.setAttribute("aria-hidden", "true");
      }
      this.#on = to;
    }
    this.#scr[to].querySelector<HTMLElement>(".body")?.scrollTo?.({ top: 0 });
    if (focus) this.#titles[to].focus({ preventScroll: true });
  }

  // ------------------------------------------------------------------ Start
  hero(h: { readonly title: string; readonly lead: string }): void {
    this.#heroTitle.textContent = h.title;
    this.#heroLead.textContent = h.lead;
  }

  /** Start's line (25i): declined (neutral), unavailable / expired / no bank (loud); and the button says Try again. */
  note(note: StartNote | undefined): void {
    this.#note.hidden = note === undefined;
    const loud = note !== undefined && note !== "declined";
    this.#note.classList.toggle("loud", loud);
    this.#note.setAttribute("role", loud ? "alert" : "status");
    const words = note === undefined ? { lead: "", rest: "" } : NOTES[note];
    this.#noteLead.textContent = words.lead;
    this.#noteRest.textContent = words.rest;
    this.#noteRest.hidden = words.rest === "";
    this.#getMyinfo.textContent = note === undefined ? KYC_COPY.getMyinfo : KYC_COPY.tryAgain;
  }

  /** The start button: busy (label kept) while the bank answers. */
  starting(on: boolean): void {
    this.#getMyinfo.setAttribute("aria-busy", on ? "true" : "false");
  }

  focusGetMyinfo(): void {
    this.#getMyinfo.focus({ preventScroll: true });
  }

  // ----------------------------------------------------- the consent prompt
  /** The placeholder rises; the screens under it are out of reach (inert, hidden); focus in the prompt, then back to its opener. */
  consent(open: boolean): void {
    if (open === this.consentOpen) return;
    if (open) {
      this.#consentFrom = document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
      this.#consent.hidden = false;
      this.#screens.inert = true;
      this.#screens.setAttribute("aria-hidden", "true");
      this.#backdrop.classList.add("in");
      // One frame hidden-and-placed, then the rise (reduced motion: the tokens make it instant).
      void this.#consent.offsetWidth;
      this.#consent.classList.add("in");
      this.#share.focus({ preventScroll: true });
      return;
    }
    this.#consent.classList.remove("in");
    this.#consent.hidden = true;
    this.#backdrop.classList.remove("in");
    this.#screens.inert = false;
    this.#screens.removeAttribute("aria-hidden");
    const back = this.#consentFrom;
    this.#consentFrom = undefined;
    if (back?.isConnected === true) back.focus({ preventScroll: true });
  }

  // ---------------------------------------------------------------- Details
  /** While Myinfo answers: a skeleton at the real heights, hidden from assistive tech, and one status line (25f). */
  retrieving(): void {
    this.#mi.replaceChildren(
      ...["38%", "30%", "42%", "86%"].map((w) => {
        const v = el("span", { class: "sk sk-v" });
        v.style.setProperty("--w", w);
        return el("div", { aria: { hidden: "true" } }, [el("span", { class: "k" }, [el("span", { class: "sk sk-k" })]), el("span", { class: "v" }, [v])]);
      }),
    );
    setBusy(this.#dBody, this.#dNote, this.#mi, true, KYC_COPY.retrieving);
    this.#mih.style.visibility = "hidden";
    this.#you.hidden = true;
    this.#skelYou.hidden = false;
    this.#review.setAttribute("aria-disabled", "true");
    this.occupationError(undefined);
    this.fundsError(undefined);
  }

  /** The details: Myinfo's rows read-only; the occupation and where money comes from as the bank holds them. */
  details(rows: readonly Row[], occupation: string, funds: readonly { readonly key: string; readonly label: string; readonly on: boolean }[]): void {
    this.#mi.replaceChildren(...rows.map(kvRow));
    setBusy(this.#dBody, this.#dNote, this.#mi, false, "");
    this.#mih.style.visibility = "";
    this.#skelYou.hidden = true;
    this.#you.hidden = false;
    this.#occ.value = occupation;
    this.#funds.replaceChildren(
      ...funds.map((f) => {
        const chip = el("button", { class: "raw chip", type: "button", data: { key: f.key }, aria: { pressed: f.on ? "true" : "false" } }, [f.label]);
        chip.addEventListener("click", () => {
          chip.setAttribute("aria-pressed", chip.getAttribute("aria-pressed") === "true" ? "false" : "true");
          if (this.chosen().length > 0) this.fundsError(undefined);
        });
        return chip;
      }),
    );
    this.#review.setAttribute("aria-disabled", "false");
  }

  /** The occupation as the customer left it. */
  get occupation(): string {
    return this.#occ.value;
  }

  /** The sources the customer chose, in the bank's order. */
  chosen(): string[] {
    return [...this.#funds.querySelectorAll<HTMLButtonElement>(".chip[aria-pressed='true']")].map((c) => c.dataset["key"] ?? "");
  }

  occupationError(text: string | undefined): void {
    this.#occErr.hidden = text === undefined;
    this.#occErr.replaceChildren(...(text === undefined ? [] : [icon("i-alert"), text]));
    this.#occBox.classList.toggle("invalid", text !== undefined);
    if (text === undefined) this.#occ.removeAttribute("aria-invalid");
    else this.#occ.setAttribute("aria-invalid", "true");
  }

  fundsError(text: string | undefined): void {
    this.#fundsErr.hidden = text === undefined;
    this.#fundsErr.replaceChildren(...(text === undefined ? [] : [icon("i-alert"), text]));
  }

  focusOccupation(): void {
    this.#occ.focus({ preventScroll: true });
  }

  // --------------------------------------------------------- Check and sign
  /** While the bank builds the card: a skeleton at the card's heights and one status line. */
  building(): void {
    this.#sign = undefined;
    this.#signLine = undefined;
    this.#cancelled = undefined;
    setBusy(this.#sBody, this.#sNote, this.#slot, true, LOADING_CARD);
    this.#slot.replaceChildren(cardSkeleton());
  }

  /**
   * 25h: the Bank card, drawn from the payload alone — every detail being declared, the bank's declaration above Sign,
   * "Sign · update details" and "Not now". There is no checkbox: the signature is the declaration.
   */
  card(card: KycCard): void {
    const sign = el("button", { class: "raw btn sign", type: "button" }, [icon("i-key"), el("span", { class: "lbl" }, [KYC_COPY.sign])]);
    // Straight through: the controller's first statement is the passkey ceremony (no await before it).
    sign.addEventListener("click", () => {
      if (sign.getAttribute("aria-busy") === "true") return;
      this.#h.onSign();
    });
    const notNow = el("button", { class: "raw btn link notnow", type: "button" }, [KYC_COPY.notNow]);
    notNow.addEventListener("click", () => this.#h.onNotNow());
    const decl = declarationParts(card.declaration);
    this.#cancelled = el("div", { class: "notice cn", role: "status", hidden: true }, [icon("i-alert"), el("div", {}, [el("b"), el("span")])]);
    this.#signLine = el("p", { class: "why", hidden: true, aria: { live: "polite" } });
    this.#sign = sign;
    const node = el("div", { class: "bank kycard", role: "group", aria: { label: "Bank: update your details, to sign" }, data: { bundle: card.bundleId } }, [
      el("div", { class: "bh" }, [el("span", { class: "banktag" }, ["Bank"]), el("b", { class: "count" }, [KYC_COPY.cardTitle])]),
      el("dl", { class: "kv" }, cardRows(card).map(kvRow)),
      el("div", { class: "bf" }, [
        this.#cancelled,
        el("div", { class: "notice decl" }, [icon("i-doc"), el("div", {}, [el("b", {}, [decl.lead]), decl.rest])]),
        this.#signLine,
        el("div", { class: "acts" }, [sign, notNow]),
      ]),
    ]);
    setBusy(this.#sBody, this.#sNote, this.#slot, false, "");
    this.#slot.replaceChildren(node);
    this.#sNote.textContent = "Bank: update your details, to sign";
  }

  /** Sign holds (busy, label kept) while the ceremony's options are fetched and while the passkey sheet is up. */
  signBusy(on: boolean): void {
    this.#sign?.setAttribute("aria-busy", on ? "true" : "false");
  }

  /** "Nothing was signed. Sign when you're ready." (25i) — or the bank's own sentence, above the same Sign. */
  signStopped(lead: string | undefined, rest = ""): void {
    const line = this.#cancelled;
    if (line === undefined) return;
    line.hidden = lead === undefined;
    const [b, span] = [line.querySelector("b"), line.querySelector("span")];
    if (b !== null) b.textContent = lead ?? "";
    if (span !== null) span.textContent = rest;
  }

  /** The line above Sign (a first signature saves a passkey first). */
  signLine(text: string | undefined): void {
    if (this.#signLine === undefined) return;
    this.#signLine.hidden = text === undefined;
    this.#signLine.textContent = text ?? "";
  }

  /** The bank could not build or show the card: what happened and Try again — never a card, never a Sign. */
  said(lead: string, rest: string, onRetry: () => void): void {
    this.#sign = undefined;
    const retry = button("ghost", KYC_COPY.tryAgain, onRetry);
    setBusy(this.#sBody, this.#sNote, this.#slot, false, "");
    this.#slot.replaceChildren(el("div", { class: "said" }, [el("div", { class: "notice" }, [icon("i-alert"), el("div", {}, [el("b", {}, [lead]), ` ${rest}`])]), el("div", { class: "acts" }, [retry])]));
    this.#sNote.textContent = `${lead}. ${rest}`;
  }

  // ------------------------------------------------------------------- Done
  done(m: DoneModel): void {
    this.#doneTitle.textContent = m.title;
    this.#doneAgain.hidden = !m.again;
    this.#doneRows.replaceChildren(...m.rows.map(kvRow));
    this.#doneRows.hidden = m.rows.length === 0;
    this.#doneSec.hidden = m.rows.length === 0;
  }
}
